import React, { useEffect } from 'react';
import { Prospect } from '../../shared/types';
import { automoveisStock } from '../../shared/data/automoveisData';
import { Car, Search, Plus } from 'lucide-react';

export const AutoSystem: React.FC<{ prospect: Prospect }> = ({ prospect }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans flex flex-col selection:bg-red-600 selection:text-white">
      
      {/* Top Navbar Auto CRM - STRICT NO SHADOWS */}
      <header className="h-20 bg-white border-b border-slate-200 px-8 flex items-center justify-between shrink-0">
         <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-red-600 text-white flex items-center justify-center font-black">
               <Car className="w-5 h-5" />
            </div>
            <div>
               <h1 className="font-heading font-black text-lg text-slate-900 uppercase tracking-wider">AutoStock — {prospect.trade_name}</h1>
               <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Gestão de Estoque & Propostas de Troca</p>
            </div>
         </div>

         <div className="flex items-center gap-4">
            <div className="relative hidden md:block">
               <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
               <input type="text" placeholder="Buscar veículo por chassi ou placa..." className="pl-9 pr-4 py-2 bg-slate-100 border border-slate-200 rounded-xl text-xs text-slate-900 outline-none w-72 placeholder:text-slate-500" />
            </div>
            <button className="bg-red-600 text-white px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 hover:bg-red-700 transition-colors border border-red-500">
               <Plus className="w-4 h-4" /> Entrada de Veículo
            </button>
         </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-y-auto">
         
         {/* Metrics Row - STRICT NO SHADOWS */}
         <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-xl border border-slate-200">
               <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Valor do Estoque Total</span>
               <span className="text-3xl font-heading font-black text-slate-900">R$ 4.69M</span>
               <p className="text-xs text-emerald-600 font-bold mt-2">4 Veículos Ativos</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-slate-200">
               <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Propostas de Troca Abertas</span>
               <span className="text-3xl font-heading font-black text-amber-600">6</span>
               <p className="text-xs text-amber-600 font-bold mt-2">Aguardando Vistoria Cautelar</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-slate-200">
               <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Margem Média Bruta</span>
               <span className="text-3xl font-heading font-black text-slate-900">14.8%</span>
               <p className="text-xs text-slate-500 mt-2">Meta: 12%</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-slate-200">
               <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Giro de Estoque Médio</span>
               <span className="text-3xl font-heading font-black text-red-600">19 Dias</span>
               <p className="text-xs text-emerald-600 font-bold mt-2">Alta Liquidez</p>
            </div>
         </div>

         {/* Stock List Table - STRICT NO SHADOWS */}
         <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center bg-slate-50">
               <h3 className="font-heading font-black text-lg text-slate-900 uppercase">Veículos em Exibição no Showroom</h3>
               <span className="text-xs font-bold text-slate-500">Atualizado em Tempo Real</span>
            </div>

            <div className="overflow-x-auto">
               <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider">
                     <tr>
                        <th className="p-4">Veículo / Versão</th>
                        <th className="p-4">Ano / Km</th>
                        <th className="p-4">Preço Anunciado</th>
                        <th className="p-4">Status de Preparação</th>
                        <th className="p-4">Ação de Vendas</th>
                     </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                     {automoveisStock.map((v) => (
                        <tr key={v.id} className="hover:bg-slate-50 transition-colors">
                           <td className="p-4">
                              <div className="flex items-center gap-3">
                                 <img src={v.image} className="w-12 h-9 rounded object-cover border border-slate-200" alt={v.model} />
                                 <div>
                                    <span className="font-bold text-slate-900 block text-sm">{v.brand} {v.model}</span>
                                    <span className="text-[10px] text-slate-500">{v.version}</span>
                                 </div>
                              </div>
                           </td>
                           <td className="p-4 font-mono font-bold text-slate-700">{v.year}<span className="block text-[10px] text-slate-500 font-sans font-normal">{v.km}</span></td>
                           <td className="p-4 font-black text-slate-900">R$ {(v.price / 1000).toLocaleString('pt-BR')}.000</td>
                           <td className="p-4">
                              <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase">
                                 ✓ Pronto no Showroom
                              </span>
                           </td>
                           <td className="p-4">
                              <button className="bg-slate-100 text-slate-700 hover:bg-slate-200 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-colors border border-slate-300">
                                 Ver Propostas
                              </button>
                           </td>
                        </tr>
                     ))}
                  </tbody>
               </table>
            </div>
         </div>

      </main>

    </div>
  );
};
